/** @jsxImportSource @emotion/react */
import React, { useCallback, useEffect, useState } from 'react';
import { Button, Icon, Modal } from 'semantic-ui-react';
import * as emailValidator from 'email-validator';
import { useTranslation } from 'react-i18next';
import style from './distributionListAssessmentField.style';
import { useCtrlEnterToRunCallback } from '../../../core/utils';
import { AdUserDropdownField } from '../../users/components/AdUserDropdownField';
import { AdUser, Contact } from '../../users/model/user.model';
import { BrandCode } from '../../../shared/model/brand.model';
import api from '../../distributionListGroup/utils/api';
import { DistributionListGroup, getEmptyGroup } from '../../distributionListGroup/models/distributionListGroup';
import { DistributionListGroupsDropDown } from './DistributionListGroupsDropDown';

interface AssessmentFieldProps {
  defaultValue?: Contact[];
  editable?: boolean;
  onEdited?: (values: Contact[]) => void;
  title?: string;
  brand?: BrandCode;
  regionDescription?: string;
  countryCode?: string;
  openModal?: boolean;
  onOk?: () => void;
  onCancel?: () => void;
}

export function DistributionListAssessmentField({
  defaultValue = [],
  editable = false,
  onEdited = () => null,
  title = '',
  brand,
  regionDescription,
  countryCode,
  openModal,
  onOk,
  onCancel
}: AssessmentFieldProps): JSX.Element {
  const { t } = useTranslation();

  const [open, setOpen] = useState(openModal ?? false);
  const [distributionList, setDistributionList] = useState<Contact[]>(defaultValue);
  const [selectedUser, setSelectedUser] = useState({ name: '', email: '', jobTitle: '' });
  const [selectedGroup, setSelectedGroup] = useState<DistributionListGroup>(getEmptyGroup());
  const [groups, setGroups] = useState<DistributionListGroup[]>([]);
  const [isFetching, setIsFetching] = useState(false);
  
  const updateValue = () => {
    onEdited(distributionList);
    if (onOk != null) {
      onOk()
    }
    setOpen(false);
  };

  const cancelValue = () => {
    setDistributionList(defaultValue);
    setSelectedUser({ name: '', email: '', jobTitle: '' });
    setSelectedGroup(getEmptyGroup())
    if (onCancel != null)  {
      onCancel()
    } 
    setOpen(false)
  };

  const getFieldDescription = useCallback(() => defaultValue.map(c => c.email).join(', '), [defaultValue]);

  const addEmail = () => {
    const state = [...distributionList];
    if (!state.find((c) => c.email === selectedUser.email)) state.push(selectedUser);
    setDistributionList(state);
    setSelectedUser({ name: '', email: '', jobTitle: '' });
  };

  const handleAddEmailsFromGroup = () => {
    const state = [...distributionList]
    selectedGroup?.distributionList.forEach(g => {
      if (!state.find((c) => c.email === g.email)) state.push(g)
    })
    setDistributionList(state);
    setSelectedGroup(getEmptyGroup());
  };


  const removeEmail = (email: string) => {
    setDistributionList(distributionList?.filter(c => c.email !== email));
  };

  const onAdUserChange = (user?: AdUser) => {
    if (user) {
      setSelectedUser({
        name: `${user.firstName} ${user.familyName}`,
        email: user.email,
        jobTitle: user.jobTitle || '',
      });
    } else {
      setSelectedUser({ name: '', email: '', jobTitle: '' });
    }
  };

  const onDistributionListGroupChange = (group?: DistributionListGroup) => {
    if (group) {
      setSelectedGroup(group);
    } else {
      setSelectedGroup(getEmptyGroup())
    }
  };


  function isValidEmail() {
    return (
      distributionList != null &&
      selectedUser.email !== '' &&
      emailValidator.validate(selectedUser.email) &&
      !distributionList.includes(selectedUser)
    );
  }

  useEffect(() => {
    const state = [...defaultValue];
    setDistributionList(state);
  }, [defaultValue, open, editable]);

  useEffect(() => {
    const fetchDistributionListGroups = () => {
        return api
        .fetchDistributionListGroupsByStoreLocation(brand as BrandCode, regionDescription as string, countryCode as string)
        .then(r => {
          if (r.length > 0) {
            setGroups(r);
          }
        })
        .catch(() => {
          setGroups([]);
        })
        .finally(() => {
          setIsFetching(false);
        });
      }
      if (editable) {
        fetchDistributionListGroups();
      }
  }, [editable, brand, regionDescription, countryCode]);

  useCtrlEnterToRunCallback(updateValue, open);

  return (
    <div>
      {!openModal && (
        <div title={title} css={editable ? style.editableField : style.field} onClick={() => editable && setOpen(true)}>
        <Icon name='mail' />
        <div css={style.valueContainer}>
          <span css={style.value}> {getFieldDescription()} </span>
          <span css={style.subTitle}>{title}</span>
        </div>
        {editable && (
          <div css={style.editIcon}>
            <Icon name='pencil' />
          </div>
        )}
        </div>
      )}
      <Modal css={style.modal} onClose={() => cancelValue()} onOpen={() => setOpen(true)} open={open}>
        <Modal.Header>{title}</Modal.Header>
        <Modal.Content css={style.modalContent}>
          <div>
            <div css={style.inputsContainer}>
            <div css={style.inputContainer}>
              <DistributionListGroupsDropDown
                groups={groups}
                value={selectedGroup.name}
                onChange={onDistributionListGroupChange}
                cornerIcon='users'
                placeholder={t('users.searchGroup') || ''}
                isFetching={isFetching}
              />
              <Button onClick={handleAddEmailsFromGroup} disabled={selectedGroup.name === ''} icon>
                <Icon name='plus'> </Icon>
              </Button>
            </div>
            <div css={style.inputContainer}>
              <AdUserDropdownField
                placeholder={t('users.searchUser') || ''}
                onChange={onAdUserChange}
                cornerIcon='user'
                value={selectedUser.email}
                showJobTitle
              />
              <Button onClick={addEmail} disabled={!isValidEmail()} icon>
                <Icon name='plus'> </Icon>
              </Button>
            </div>
            </div>
            <div css={style.resultContainer}>
              {distributionList.map(contact => (
                <div css={style.checkBoxContainer} key={contact.email}>
                  <Icon name='mail' />
                  <div css={style.distributionListMemberInfo}>
                    <div>{contact.email}</div>
                    <div css={style.distributionListMemberName}>{contact.name}</div>
                    {contact.jobTitle && <div css={style.distributionListMemberName}>{contact.jobTitle}</div>}
                  </div>
                  <Button icon onClick={() => removeEmail(contact.email)}>
                    <Icon name='remove' />
                  </Button>
                </div>
              ))}
            </div>
          </div>
        </Modal.Content>
        <Modal.Actions css={style.actions}>
          {editable && (
            <Button css={style.button} onClick={() => cancelValue()}>
              <Icon name='cancel' /> Cancel
            </Button>
          )}
          <Button css={style.button} onClick={() => updateValue()}>
            <Icon name='checkmark' /> Ok
          </Button>
        </Modal.Actions>
      </Modal>
    </div>
  );
}
